import { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useDropzone } from 'react-dropzone';
import apiClient from '../api/client';
import { Upload, CheckCircle, AlertCircle, X } from 'lucide-react';

export default function EvidenceUpload() {
  const [params] = useSearchParams();

  const [caseNumber, setCaseNumber] = useState(params.get('case') || '');
  const [description, setDescription] = useState('');
  const [evidenceType, setEvidenceType] = useState('Document');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  const { data: cases } = useQuery({
    queryKey: ['cases'],
    queryFn: () => apiClient.getCases(),
  });

  const onDrop = useCallback((accepted: File[]) => {
    if (accepted.length > 0) {
      setFile(accepted[0]);
      setResult(null);

      // Auto-detect evidence type from file
      const type = accepted[0].type;
      if (type.startsWith('video/')) setEvidenceType('Video');
      else if (type.startsWith('audio/')) setEvidenceType('Audio');
      else if (type.startsWith('image/')) setEvidenceType('Image');
      else setEvidenceType('Document');
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
  });

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !caseNumber) return;

    setUploading(true);
    setProgress(0);
    setResult(null);

    try {
      const evidence = await apiClient.uploadEvidence(
        caseNumber,
        file,
        { description, evidenceType },
        setProgress
      );
      setResult({ success: true, message: `Uploaded: ${evidence.id} — SHA-256 hash: ${evidence.sha256Hash.slice(0, 16)}...` });
      setFile(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      setResult({ success: false, message });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-white">Upload Evidence</h1>

      <form onSubmit={handleUpload} className="space-y-5">
        {/* Case selector */}
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">Case</label>
          <select
            value={caseNumber}
            onChange={(e) => setCaseNumber(e.target.value)}
            className="w-full px-4 py-2.5 bg-rtcc-panel border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-rtcc-accent"
            required
          >
            <option value="">Select case...</option>
            {cases?.map((c) => (
              <option key={c.caseNumber} value={c.caseNumber}>
                {c.caseNumber} — {c.title}
              </option>
            ))}
          </select>
        </div>

        {/* Drop zone */}
        <div
          {...getRootProps()}
          className={`p-8 border-2 border-dashed rounded-xl text-center cursor-pointer transition-colors ${
            isDragActive
              ? 'border-rtcc-accent bg-rtcc-accent/5'
              : file
              ? 'border-rtcc-accent/50 bg-rtcc-panel'
              : 'border-slate-600 bg-rtcc-panel hover:border-slate-500'
          }`}
        >
          <input {...getInputProps()} />
          {file ? (
            <div className="flex items-center justify-center gap-3">
              <CheckCircle className="h-5 w-5 text-rtcc-accent" />
              <span className="text-white">{file.name}</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setFile(null); }}
                className="text-slate-400 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <>
              <Upload className="h-8 w-8 text-slate-500 mx-auto mb-2" />
              <p className="text-slate-400 text-sm">
                Drop video, audio, images, or documents here
              </p>
              <p className="text-slate-500 text-xs mt-1">or click to browse</p>
            </>
          )}
        </div>

        {/* Type & Description */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">Type</label>
            <select
              value={evidenceType}
              onChange={(e) => setEvidenceType(e.target.value)}
              className="w-full px-4 py-2.5 bg-rtcc-panel border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-rtcc-accent"
            >
              <option value="Video">Video</option>
              <option value="Audio">Audio</option>
              <option value="Image">Image</option>
              <option value="Document">Document</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-4 py-2.5 bg-rtcc-panel border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-rtcc-accent"
              placeholder="Brief description..."
            />
          </div>
        </div>

        {/* Progress bar */}
        {uploading && (
          <div className="w-full bg-slate-700 rounded-full h-2">
            <div
              className="bg-rtcc-accent h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}

        {/* Result */}
        {result && (
          <div className={`flex items-start gap-2 p-3 rounded-lg text-sm ${
            result.success ? 'bg-rtcc-accent/10 text-rtcc-accent' : 'bg-red-900/20 text-red-400'
          }`}>
            {result.success ? <CheckCircle className="h-4 w-4 mt-0.5" /> : <AlertCircle className="h-4 w-4 mt-0.5" />}
            {result.message}
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={uploading || !file || !caseNumber}
          className="w-full py-3 bg-rtcc-accent text-white font-medium rounded-lg hover:bg-rtcc-accent/90 disabled:opacity-50 transition-colors"
        >
          {uploading ? `Uploading... ${progress}%` : 'Upload & Hash'}
        </button>
      </form>
    </div>
  );
}
